import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { makeStyles } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { CostCenterDetail, PlanUnit } from '../../../types';

import { convertFlagsArrayToObjects } from 'utils/misc';
import { handleSaveCostCenterDetails } from '../../utils/handleSaveCostCenterDetails';
import {
  apiToFormAdapter,
  formToApiAdapterBase,
  formatCostCenterDetailLabel,
  mapFormFlagsForApi
} from '../utils/utils';

import { Grid } from '@mui/material';
import Box from '@mui/material/Box';

import EditPage from '../../../../../../../shared/CRUD/components/InnerPage/EditPage';

import { TableCRUDLayoutProps } from 'components/shared/CRUD/TableCRUDLayout';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import useForceRefresh from '../../../../../../../shared/hooks/useForceRefresh';
import useEditPlanUnit from '../../../hooks/useEditPlanUnit';
import useGetCostCenterDetailsById from '../../../hooks/useGetCostCenterDetailsById';
import { editPlanUnitSchema } from '../planUnitSchema';
import CostCenterDetailsTable from './CostCenterDetailsTable';
import PlanUnitFormFields from './PlanUnitFormFields';
import { HierarchyPlanUnit } from '../../utils/treeHierarchy';

interface Props {
  setIsInnerLayoutOpen: (isOpen: boolean) => void;
  toggleInnerLayout: () => void;
  onSaveChanges?: () => void;
  rowData: PlanUnit;
  rows: TableCRUDLayoutProps<PlanUnit | HierarchyPlanUnit>['rows'];
}

const useStyles = makeStyles(() => ({
  root: {
    flexDirection: 'column',
    flex: 1
  }
}));

const EditPlanUnit: React.FC<Props> = ({
  onSaveChanges,
  rowData,
  rows,
  toggleInnerLayout
}) => {
  const classes = useStyles();
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const handleOnCancel = () => {
    setDeletedIds([]);
    methods.reset();
  };

  const [currentViewData, setCurrentViewData] = useState<PlanUnit>(rowData);

  const { timestamp: isEditable, refresh: setIsEditable } = useForceRefresh();

  const methods = useForm({
    defaultValues: editPlanUnitSchema.cast(apiToFormAdapter(currentViewData)),
    resolver: yupResolver(editPlanUnitSchema),
    mode: 'onChange'
  });

  const { mutateAsync } = useEditPlanUnit();

  const { data: costCenterDetails } = useGetCostCenterDetailsById(
    currentViewData.brId,
    (response) => {
      return response.data.map((costCenter) => ({
        ...costCenter,
        flags: convertFlagsArrayToObjects(costCenter.flags)
      }));
    }
  );

  useEffect(() => {
    if (costCenterDetails) {
      methods.reset({
        ...editPlanUnitSchema.cast({
          ...apiToFormAdapter(currentViewData),
          costCenterDetails: costCenterDetails.map(
            ({ finBrDynId, parentBrDynId, ...rest }) => ({
              ...rest,
              finBrDynId: finBrDynId
                ? {
                    value: finBrDynId,
                    label: formatCostCenterDetailLabel(
                      rest as CostCenterDetail,
                      'financialBrName'
                    )
                  }
                : null,
              parentBrDynId: parentBrDynId
                ? {
                    value: parentBrDynId,
                    label: formatCostCenterDetailLabel(
                      rest as CostCenterDetail,
                      'parentBrName'
                    )
                  }
                : null
            })
          )
        })
      });
    }
  }, [costCenterDetails, currentViewData]);

  const onSubmit = (data) => {
    mutateAsync(formToApiAdapterBase(data)).then(() => {
      handleSaveCostCenterDetails(
        {
          ...data,
          costCenterDetails: data.costCenterDetails.map((costCenterDetail) => ({
            ...costCenterDetail,
            flags: mapFormFlagsForApi(costCenterDetail.flags)
          }))
        },
        deletedIds
      )
        .then(async () => {
          showSuccessSnackbar();
          setIsEditable();
          setDeletedIds([]);
          methods.reset(data);
          onSaveChanges?.();
          await queryClient.invalidateQueries(['costCenterDetails']);
        })
        .catch((error) => {
          showErrorSnackbar(error);
        });
    });
  };

  return (
    <EditPage
      onCancel={handleOnCancel}
      toggleInnerLayout={toggleInnerLayout}
      currentView={currentViewData}
      setCurrentView={setCurrentViewData}
      views={rows}
      isEditable={isEditable}
      methods={methods}
      onSubmit={onSubmit}
      navigatorValueGetter={'brId'}
      navigatorLabelGetter={['name', 'code']}
    >
      <Box display="flex" className={classes.root} m={2}>
        <Box mb={2.5} sx={{ width: '100%' }}>
          <Grid container mb={2.5} rowSpacing={2} columnSpacing={2}>
            <PlanUnitFormFields />
          </Grid>
        </Box>

        <CostCenterDetailsTable setDeletedIds={setDeletedIds} />
      </Box>
    </EditPage>
  );
};

export default EditPlanUnit;
